import {
  MINTED_PUBLIC_ID_SUFFIX_LENGTH,
  RESOURCE_TYPE_TO_PREFIX,
  isDeclaredPublicId,
  isMintedPublicIdOfType,
  isPublicIdOfType,
  leadingMintedPublicId,
  resourceTypeFromPublicId,
  type PublicId,
  type PublicIdResourceType,
} from "./public-id";

/**
 * A value format checked at a boundary (tool input, API request). The spec
 * carries the resolution so every rejection can say what is wrong AND how to
 * fix it, without each boundary composing its own sentence.
 */
export interface FormatSpec<T = string> {
  /** Short noun for messages, e.g. `"integration id"`. */
  name: string;
  test: (value: unknown) => value is T;
  /** The shape a correct value takes, e.g. `"int_…"`. */
  example: string;
  /** Emitted as `x-id-kind` on an id's JSON schema; set by every id format, absent elsewhere. */
  idKind?: string;
  /** Emitted as the JSON schema `pattern` so models and API clients see the form; `test` stays authoritative. */
  pattern?: RegExp;
  /** One imperative sentence. */
  howToResolve: string;
  /**
   * Where a valid value comes from, when the spec knows ("Call list_tasks …").
   * A blank required value's message ends with it.
   */
  whereToFind?: string;
  /**
   * A sharper "what is wrong" for a value the spec can classify (e.g. an id of
   * a different resource type), with its own resolution when the spec's would
   * mislead. Returns undefined to use the default.
   */
  describeMismatch?: (value: unknown) => string | FormatMismatch | undefined;
}

export type FormatMismatch = { what: string; howToResolve: string };

export const FORMAT_ERROR_CODE = "invalid_format";
const RECEIVED_PREVIEW_LENGTH = 40;

/** `value` rendered for a message: JSON, truncated so a dumped document cannot swamp it. */
export function previewValue(value: unknown): string {
  const text = JSON.stringify(value) ?? String(value);
  return text.length > RECEIVED_PREVIEW_LENGTH
    ? `${text.slice(0, RECEIVED_PREVIEW_LENGTH)}…`
    : text;
}

export class FormatError extends Error {
  readonly code = FORMAT_ERROR_CODE;
  readonly what: string;
  readonly howToResolve: string;
  readonly received: string;
  /** Where the value sat in its request or tool input, when the checker knew. */
  readonly path: readonly PropertyKey[];

  constructor(input: {
    what: string;
    howToResolve: string;
    received: string;
    path?: readonly PropertyKey[];
  }) {
    super(`${input.what} ${input.howToResolve}`);
    this.name = "FormatError";
    this.what = input.what;
    this.howToResolve = input.howToResolve;
    this.received = input.received;
    this.path = input.path ?? [];
  }
}

export function isFormatError(error: unknown): error is FormatError {
  return (
    error instanceof FormatError ||
    (error instanceof Error && (error as { code?: unknown }).code === FORMAT_ERROR_CODE)
  );
}

export type FormatCheck<T> = { ok: true; value: T } | { ok: false; error: FormatError };

/**
 * An id that is blank, or a valid one wrapped in whitespace: one sentence for
 * every id kind, so a query, path, body or tool input says the same thing.
 * Nothing is trimmed anywhere; a stray space is how a garbled call shows up.
 */
function idEdgeMismatch<T>(
  value: unknown,
  spec: FormatSpec<T>,
  optional: boolean,
): FormatMismatch | undefined {
  if (typeof value !== "string") return undefined;
  const clean = value.trim();
  if (!clean) {
    // Agents act on the text literally: "omit" only where omitting is allowed.
    const pass = `Pass ${withArticle(spec.name)} (${spec.example})`;
    return {
      what: "is empty.",
      howToResolve: optional
        ? `${pass}, or omit the parameter.`
        : `${pass}.${spec.whereToFind ? ` ${spec.whereToFind}` : ""}`,
    };
  }
  if (clean !== value && spec.test(clean)) {
    return {
      what: `${previewValue(value)} has extra characters around the id; did you mean ${clean}?`,
      howToResolve: "Resend exactly that id.",
    };
  }
  return undefined;
}

export function checkFormat<T>(
  value: unknown,
  spec: FormatSpec<T>,
  opts: { path?: readonly PropertyKey[]; optional?: boolean } = {},
): FormatCheck<T> {
  if (spec.test(value)) return { ok: true, value };
  const received = previewValue(value);
  const mismatch =
    (spec.idKind && idEdgeMismatch(value, spec, opts.optional === true)) ||
    spec.describeMismatch?.(value);
  const what =
    (typeof mismatch === "object" ? mismatch.what : mismatch) ??
    `${received} is not a valid ${spec.name} (expected the form ${spec.example}).`;
  return {
    ok: false,
    error: new FormatError({
      what,
      howToResolve: typeof mismatch === "object" ? mismatch.howToResolve : spec.howToResolve,
      received,
      ...(opts.path ? { path: opts.path } : {}),
    }),
  };
}

export function assertFormat<T>(
  value: unknown,
  spec: FormatSpec<T>,
  opts: { path?: readonly PropertyKey[] } = {},
): T {
  const result = checkFormat(value, spec, opts);
  if (!result.ok) throw result.error;
  return result.value;
}

// "us…" is a consonant sound ("a user id"), unlike "an upload".
export function withArticle(noun: string): string {
  return `${/^(?:[aeio]|u(?!s))/i.test(noun) ? "an" : "a"} ${noun}`;
}

/**
 * A public id of one registered resource type, or of any of several when a
 * parameter legitimately takes more than one (an integration grant is a
 * workspace integration or a linked account). The body must be the full
 * minted shape, so a truncated id or one with junk after it is rejected here
 * instead of being looked up and reported as missing.
 */
export function publicIdFormat<T extends PublicIdResourceType>(
  type: T | readonly T[],
  opts: { label?: string; howToResolve?: string } = {},
): FormatSpec<PublicId<T>> {
  const types: readonly T[] = Array.isArray(type) ? (type as readonly T[]) : [type as T];
  const prefixes = types.map((t) => RESOURCE_TYPE_TO_PREFIX[t]);
  const label = opts.label ?? types.map((t) => t.replaceAll("_", " ")).join(" or ");
  const example = prefixes.map((p) => `${p}_…`).join(" or ");
  const name = `${label} id`;
  const alternation = prefixes.length === 1 ? prefixes[0] : `(?:${prefixes.join("|")})`;
  return {
    name,
    example,
    idKind: `public:${types.join("|")}`,
    pattern: new RegExp(`^${alternation}_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}$`),
    howToResolve: opts.howToResolve ?? `Pass ${withArticle(name)} in the form ${example}.`,
    ...(opts.howToResolve ? { whereToFind: opts.howToResolve } : {}),
    test: (value): value is PublicId<T> =>
      typeof value === "string" &&
      types.some(
        (t) =>
          isMintedPublicIdOfType(value, t) ||
          (isDeclaredPublicId(value) && isPublicIdOfType(value, t)),
      ),
    describeMismatch: (value) => {
      if (typeof value !== "string") return undefined;
      const actual = resourceTypeFromPublicId(value);
      if (!actual) return undefined;
      const received = previewValue(value);
      if (!(types as readonly PublicIdResourceType[]).includes(actual)) {
        return `${received} is ${withArticle(`${actual.replaceAll("_", " ")} id`)}, not ${withArticle(name)}.`;
      }
      const clean = leadingMintedPublicId(value);
      if (clean) {
        return {
          what: `${received} has extra characters after the id; did you mean ${clean}?`,
          howToResolve: "Resend exactly that id.",
        };
      }
      return `${received} is not a complete ${name} (expected ${RESOURCE_TYPE_TO_PREFIX[actual]}_ followed by ${MINTED_PUBLIC_ID_SUFFIX_LENGTH} lowercase hex characters).`;
    },
  };
}
